import { useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Divider, Popper, styled } from '@mui/material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import GridOffRoundedIcon from '@mui/icons-material/GridOffRounded'
import { AddColumnLeftIcon } from '../../../icons/AddColumnLeftIcon'
import { AddColumnRightIcon } from '../../../icons/AddColumnRightIcon'
import { AddRowAboveIcon } from '../../../icons/AddRowAboveIcon'
import { AddRowBelowIcon } from '../../../icons/AddRowBelowIcon'
import { Editor } from '@tiptap/react'
import { CellSelection } from '@tiptap/pm/tables'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import { Menu, MenuItem, MenuItemText, PopperRoot } from '../../../menu'
import { useTableContextMenu } from './useTableContextMenu'
import { isPosInCellSelection } from './isPosInCellSelection'

type InsertKind = 'rowAbove' | 'rowBelow' | 'columnLeft' | 'columnRight'
type DeleteKind = 'row' | 'column'

type Props = {
  editor: Editor | null
  containerEl: HTMLElement | null
}

const createVirtualAnchorEl = (position: { top: number; left: number }) => ({
  getBoundingClientRect: () => ({
    top: position.top,
    left: position.left,
    bottom: position.top,
    right: position.left,
    width: 0,
    height: 0,
    x: position.left,
    y: position.top,
    toJSON: () => ({}),
  }),
})

// セル右クリックメニュー。アクティブセルのハイライトは
// activeCellHighlightExtension（ProseMirror plugin）が担う。
export const TableInteractions = ({ editor, containerEl }: Props) => {
  const { contextMenu, closeContextMenu, menuRootRef } = useTableContextMenu(
    editor,
    containerEl
  )

  const runInsert = useCallback(
    (kind: InsertKind) => {
      if (!editor || !contextMenu) return
      const pos = editor.view.posAtDOM(contextMenu.cell, 0)
      if (pos < 0) return
      const { selection } = editor.state
      // 既存の CellSelection（複数セル選択）が右クリックセルを含むときは
      // それを維持して挿入すると、選択範囲の上端の上 / 下端の下（列も同様）に
      // 追加できる。範囲外の場合だけ右クリックセルへ移してそのセル基準で挿入する。
      const keepSelection =
        selection instanceof CellSelection &&
        isPosInCellSelection(selection, pos)
      if (!keepSelection) editor.commands.setTextSelection(pos)
      // 選択範囲の行数/列数分だけ追加する。chain で addRowBefore/addColumnBefore 等を
      // 複数回連鎖させると、prosemirror-tables の addColumn/addRow が tr.mapping を
      // 蓄積した状態で新 doc の position に再マッピングしてしまい、挿入位置がずれる
      // （特に columnLeft では選択セルの間に新列が紛れ込む）。command を1回ずつ個別に
      // dispatch することで tr.mapping の蓄積を回避する。
      const isRow = kind === 'rowAbove' || kind === 'rowBelow'
      const count = isRow ? contextMenu.rowCount : contextMenu.columnCount
      for (let i = 0; i < count; i += 1) {
        if (kind === 'rowAbove') editor.commands.addRowBefore()
        else if (kind === 'rowBelow') editor.commands.addRowAfter()
        else if (kind === 'columnLeft') editor.commands.addColumnBefore()
        else editor.commands.addColumnAfter()
      }
      // 追加が完了したら CellSelection を解除して単一カーソルに collapse する
      editor.commands.setTextSelection(editor.state.selection.from)
      editor.commands.focus()
      closeContextMenu()
    },
    [editor, contextMenu, closeContextMenu]
  )

  const runDelete = useCallback(
    (kind: DeleteKind) => {
      if (!editor || !contextMenu) return
      const pos = editor.view.posAtDOM(contextMenu.cell, 0)
      if (pos < 0) return
      const { selection } = editor.state
      // 既存の CellSelection（複数セル選択）が右クリックセルを含むときは
      // それを維持して削除すると複数行/列を一括削除できる。範囲外の場合だけ
      // 右クリックセルへ移してそのセル単位で削除する。
      const keepSelection =
        selection instanceof CellSelection &&
        isPosInCellSelection(selection, pos)
      const chain = editor.chain().focus()
      if (!keepSelection) chain.setTextSelection(pos)
      if (kind === 'row') chain.deleteRow()
      else chain.deleteColumn()
      chain.run()
      closeContextMenu()
    },
    [editor, contextMenu, closeContextMenu]
  )

  const runDeleteTable = useCallback(() => {
    if (!editor) return
    editor.chain().focus().deleteTable().run()
    closeContextMenu()
  }, [editor, closeContextMenu])

  const contextAnchorEl = useMemo(() => {
    if (!contextMenu) return null
    return createVirtualAnchorEl(contextMenu.position)
  }, [contextMenu])

  // ヘッダー行は常に最上段であるべきため、ヘッダーセル上では行関連の操作を制限する
  const isHeaderCell = contextMenu?.cell.tagName === 'TH'
  // 削除によってデータ行が0になる（=表が消える or ヘッダーだけ残る）場合、および
  // 列がすべて消える場合はdisabled にしてクリックを抑止する。
  const deleteRowDisabled =
    (contextMenu?.rowCount ?? 1) >= (contextMenu?.dataRowCount ?? 1)
  const deleteColumnDisabled =
    (contextMenu?.columnCount ?? 1) >= (contextMenu?.tableColumnCount ?? 1)

  return createPortal(
    <>
      <Popper
        open={!!contextAnchorEl}
        anchorEl={contextAnchorEl}
        placement="bottom-start"
        style={{ zIndex: 9999 }}
      >
        <PopperRoot ref={menuRootRef}>
          <Menu>
            {!isHeaderCell && (
              <MenuItem onClick={() => runInsert('rowAbove')}>
                <MenuItemIcon>
                  <AddRowAboveIcon sx={{ fontSize: 16 }} />
                </MenuItemIcon>
                <MenuItemText>
                  {intl.formatMessage(
                    { id: 'richText.tableMenu.insertRowAbove' },
                    { count: contextMenu?.rowCount ?? 1 }
                  )}
                </MenuItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => runInsert('rowBelow')}>
              <MenuItemIcon>
                <AddRowBelowIcon sx={{ fontSize: 16 }} />
              </MenuItemIcon>
              <MenuItemText>
                {intl.formatMessage(
                  { id: 'richText.tableMenu.insertRowBelow' },
                  { count: contextMenu?.rowCount ?? 1 }
                )}
              </MenuItemText>
            </MenuItem>
            <MenuItem onClick={() => runInsert('columnLeft')}>
              <MenuItemIcon>
                <AddColumnLeftIcon sx={{ fontSize: 16 }} />
              </MenuItemIcon>
              <MenuItemText>
                {intl.formatMessage(
                  { id: 'richText.tableMenu.insertColumnLeft' },
                  { count: contextMenu?.columnCount ?? 1 }
                )}
              </MenuItemText>
            </MenuItem>
            <MenuItem onClick={() => runInsert('columnRight')}>
              <MenuItemIcon>
                <AddColumnRightIcon sx={{ fontSize: 16 }} />
              </MenuItemIcon>
              <MenuItemText>
                {intl.formatMessage(
                  { id: 'richText.tableMenu.insertColumnRight' },
                  { count: contextMenu?.columnCount ?? 1 }
                )}
              </MenuItemText>
            </MenuItem>
            {!isHeaderCell && (
              <MenuItem
                onClick={() => runDelete('row')}
                disabled={deleteRowDisabled}
              >
                <MenuItemIcon>
                  <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                </MenuItemIcon>
                <MenuItemText>
                  {intl.formatMessage(
                    { id: 'richText.tableMenu.deleteRow' },
                    { count: contextMenu?.rowCount ?? 1 }
                  )}
                </MenuItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => runDelete('column')}
              disabled={deleteColumnDisabled}
            >
              <MenuItemIcon>
                <DeleteRoundedIcon sx={{ fontSize: 16 }} />
              </MenuItemIcon>
              <MenuItemText>
                {intl.formatMessage(
                  { id: 'richText.tableMenu.deleteColumn' },
                  { count: contextMenu?.columnCount ?? 1 }
                )}
              </MenuItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={runDeleteTable}>
              <MenuItemIcon>
                <GridOffRoundedIcon sx={{ fontSize: 16 }} />
              </MenuItemIcon>
              <MenuItemText>
                {intl.formatMessage({ id: 'richText.tableMenu.deleteTable' })}
              </MenuItemText>
            </MenuItem>
          </Menu>
        </PopperRoot>
      </Popper>
    </>,
    document.body
  )
}

const MenuItemIcon = styled('span')({
  display: 'flex',
  alignItems: 'center',
  color: colorPalette.monotone[5],
})
